// What the dashboard's widgets read, and how they keep it current.
//
// Every read goes through a route that already checks permissions; nothing here
// decides what anyone may see. Widgets that draw the same answer share one
// request, and each refreshes itself when one of its live topics changes.

import { useCallback, useEffect, useRef } from 'react';

import {
  XomicsApiError, loadAnalytics, loadCollectionActivations, loadCollectionActivity, loadCommerceAnalytics, loadCommerceEconomics,
  loadLaboratoryApprovals, loadLaboratorySamples, loadLaboratoryWork, loadMobileView, loadNotifications, loadOrderBoard,
  loadOverview, loadPipelineCatalogue, loadRecents, loadReportAnalytics, loadReportRequests, loadRequestedUploads, loadShipments,
  loadStorefrontCampaigns, loadStorefrontLive, loadStorefrontSites, loadStorefrontView, loadValidations, loadXomicsJobs, loadAuditSummary,
} from './api';
import { hasTx, tx } from './i18n';
import { useLiveResource, useLiveTopics } from './useLive';

export const MINUTE = 60000;
export const HOUR = 3600000;
export const DAY = 86400000;

// The dashboard grid's row height and the gap between tiles, in pixels. The widget
// catalogue's heights count these rows.
export const GRID = { rowHeight: 96, gap: 16 };
export const tilePixels = (rows) => (rows * (GRID.rowHeight + GRID.gap)) - GRID.gap;

export const t = (key, values) => tx(`ui.dash.${key}`, values);

// ------------------------------------------------------------------ one request, many widgets

const inflight = new Map();
const SHARE_MS = 2500;

// Widgets asking for the same answer within a moment of each other share it.
export function shared(key, load) {
  const now = Date.now();
  const hit = inflight.get(key);
  if (hit && now - hit.at < SHARE_MS) return hit.promise;
  const promise = load();
  inflight.set(key, { at: now, promise });
  promise.catch(() => { if (inflight.get(key)?.promise === promise) inflight.delete(key); });
  return promise;
}

// Some routes write an audit row on every read (Modules/audit/Catalog.js marks
// them `force`). Their widgets still follow their topics, but read again at most
// once in this long, so a dashboard left open does not fill the audit log.
export const AUDITED_GAP_MS = 2 * MINUTE;

/**
 * A widget's data: read once, then again whenever one of `topics` changes, never
 * more often than `gapMs`, and on the portal's slow reconciliation beat besides.
 * `load` must be stable (a module function, or useCallback).
 */
export function useWidgetData(topics, key, load, { gapMs = 3000, reconcileMs = 5 * MINUTE } = {}) {
  const read = useCallback(() => shared(key, load), [key, load]);
  const resource = useLiveResource([], read, { reconcileMs });
  const last = useRef(0);
  const timer = useRef(null);
  useEffect(() => { if (resource.data !== null) last.current = Date.now(); }, [resource.data]);
  const refresh = resource.refresh;
  useLiveTopics(topics, () => {
    if (timer.current !== null) return;
    const wait = Math.max(0, last.current + gapMs - Date.now());
    timer.current = window.setTimeout(() => {
      timer.current = null;
      last.current = Date.now();
      refresh();
    }, wait);
  });
  useEffect(() => () => { if (timer.current !== null) window.clearTimeout(timer.current); }, []);
  return resource;
}

// ------------------------------------------------------------------ numbers and time

export const NUMBER = new Intl.NumberFormat();
export const COMPACT = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });
export const PERCENT = new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 1 });

// `whole` drops the minor units, as a chart's axis shows money.
export function money(amountMinor, currency, minorUnit, { compact = false, whole = false } = {}) {
  const amount = Number(amountMinor) / (10 ** Number(minorUnit));
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency, currencyDisplay: 'narrowSymbol',
      ...(compact && Math.abs(amount) >= 10000 ? { notation: 'compact', maximumFractionDigits: 1 }
        : { minimumFractionDigits: 0, maximumFractionDigits: whole || Math.abs(amount) >= 1000 ? 0 : 2 }),
    }).format(amount);
  } catch (_error) {
    return `${currency} ${amount.toLocaleString()}`;
  }
}

export function range(days, now = Date.now()) {
  const to = now + 1;
  return { from: to - (days * DAY), to };
}

// Daily points from a sparse series: every day of the last `days`, quiet days as zero.
export function daily(points, days, pick, now = Date.now()) {
  const byDay = new Map();
  for (const point of points) {
    const bucket = Math.floor(point.at / DAY);
    byDay.set(bucket, (byDay.get(bucket) || 0) + pick(point));
  }
  const today = Math.floor(now / DAY);
  return Array.from({ length: days }, (_, index) => {
    const bucket = today - (days - 1) + index;
    return { at: bucket * DAY, value: byDay.get(bucket) || 0 };
  });
}

// The last seven days against the seven before, from a daily list.
export function week(values) {
  const sum = (list) => list.reduce((total, value) => total + value, 0);
  return { current: sum(values.slice(-7)), previous: sum(values.slice(-14, -7)), total: sum(values) };
}

export function change(current, previous) {
  if (previous === null || previous === undefined || !Number.isFinite(previous)) return null;
  if (previous === 0) return current === 0 ? 0 : null;
  return (current - previous) / previous;
}

export function ago(at, now = Date.now()) {
  if (at === null || at === undefined || !Number.isFinite(Number(at)) || Number(at) <= 0) return t('time.never');
  const minutes = Math.max(0, Math.floor((now - Number(at)) / MINUTE));
  if (minutes < 1) return t('time.just_now');
  if (minutes < 60) return t('time.minutes', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('time.hours', { count: hours });
  const days = Math.floor(hours / 24);
  return days < 30 ? t('time.days', { count: days })
    : new Date(Number(at)).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export function counted(key, count, values = {}) {
  return t(count === 1 ? `${key}_one` : key, { ...values, count: NUMBER.format(count) });
}

export function has(permissions, code) {
  return permissions.includes(code);
}

// A role's name as the portal writes it. APM import has no entry in the role
// vocabulary; the dashboard's own role words have had it since the first dashboard.
export function roleName(code) {
  const key = `ui.roles.${String(code || '').toLowerCase()}`;
  if (hasTx(key)) return tx(key);
  const kicker = `ui.dashboard.roles.${String(code || '').toLowerCase()}.kicker`;
  return hasTx(kicker) ? tx(kicker) : String(code || '');
}

// ------------------------------------------------------------------ the reads

// The store the dashboard speaks for: the first active site.
async function storeSite() {
  const sites = await shared('store-sites', loadStorefrontSites);
  return sites.find((site) => site.isActive) || sites[0] || null;
}

export async function readStoreOverview(days) {
  const site = await storeSite();
  if (site === null) return { site: null, report: null };
  return { site, report: await loadStorefrontView('overview', { site: site.code, days }) };
}
export const readStoreWeek = () => readStoreOverview(7);
export const readStoreMonth = () => readStoreOverview(30);

export async function readStoreLive() {
  const site = await storeSite();
  return { site, live: site === null ? null : await loadStorefrontLive(site.code) };
}

export async function readStoreCampaigns() {
  const site = await storeSite();
  return { site, campaigns: site === null ? null : await loadStorefrontCampaigns({ site: site.code, days: 30 }) };
}

// Money in the currency most payments were made in over the last 30 days; the
// others are named, never added together.
export async function readRevenue() {
  const window = range(30);
  const economics = await loadCommerceEconomics(window);
  const currencies = [...economics.currencies].sort((a, b) => b.succeededCount - a.succeededCount || a.currency.localeCompare(b.currency));
  if (currencies.length === 0) return { currencies, analytics: null };
  return { currencies, analytics: await loadCommerceAnalytics({ ...window, currency: currencies[0].currency }) };
}

export const readOverview = () => loadOverview();
export const readMobile = () => loadMobileView('overview', 30);
export const readAnalyses = () => loadAnalytics(30);
export const readReportAnalytics = () => loadReportAnalytics({ days: 30 });
export const readNotifications = () => loadNotifications({ limit: 8 });
export const readRecents = () => loadRecents(6);
export const readPipelines = () => loadPipelineCatalogue({ scope: 'active' });
export const readJobs = () => loadXomicsJobs({ status: 'all', limit: 6 });
export const readRequestedUploads = () => loadRequestedUploads();
// The laboratory's reads answer only for an account that belongs to a laboratory
// (Modules/LaboratoryInventory.js, LaboratoryReceiving.js). For anyone else that
// answer is a fact about their account, which the widget says, not a failure.
const NOT_A_LABORATORY = ['ACCOUNT_LOCATION_NOT_LABORATORY', 'SCAN_ACCOUNT_NOT_LABORATORY'];
async function atLaboratory(read) {
  try {
    return await read();
  } catch (error) {
    if (error instanceof XomicsApiError && NOT_A_LABORATORY.includes(error.code)) return { notLaboratory: true };
    throw error;
  }
}
export const readLabSamples = () => atLaboratory(() => loadLaboratorySamples({ limit: 100, status: 'all' }));
export const readLabWork = () => atLaboratory(() => loadLaboratoryWork({ limit: 1, status: 'all' }));
export const readLabApprovals = () => atLaboratory(() => loadLaboratoryApprovals());
export const readAudit = () => loadAuditSummary({ since: Date.now() - DAY });

export async function readReportQueue() {
  const [queued, processing] = await Promise.all([
    loadReportRequests({ status: 'queued', limit: 5 }),
    loadReportRequests({ status: 'processing', limit: 5 }),
  ]);
  return { statusCounts: queued.statusCounts, requests: [...processing.reportRequests, ...queued.reportRequests].slice(0, 6) };
}

// Reads whose scope follows what the viewer may see everywhere or only locally.
export function scoped(permissions) {
  const everywhere = (code) => has(permissions, code);
  return {
    kits: everywhere('KIT_READ_ALL') ? 'global' : 'local',
    orders: everywhere('ORDER_READ_ALL') ? 'global' : 'local',
    shipments: everywhere('SHIPMENT_READ_ALL') ? 'global' : 'local',
    validationsGlobal: everywhere('SAMPLE_VALIDATE_GLOBAL'),
  };
}

export const activityReader = (scope) => () => loadCollectionActivity({ scope, days: 30 });
export const activationsReader = (scope) => () => loadCollectionActivations({ scope, limit: 6 });
export const ordersReader = (scope) => () => loadOrderBoard({ ...range(30), scope });
export const shipmentsReader = (scope) => () => loadShipments({ scope: 'active', locationScope: scope, limit: 5 });
export const validationsReader = (global) => async () => {
  const [local, everywhere] = await Promise.all([
    loadValidations({ scope: 'local', status: 'pending', limit: 6 }),
    global ? loadValidations({ scope: 'global', status: 'pending', limit: 1 }) : Promise.resolve(null),
  ]);
  return { local, global: everywhere };
};
